import { useState, useEffect } from 'react';
import { notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { buildSearchQueryWithDefaultOrder } from '../utils/filters/formatSearchData';
import { getInvitationsFilters } from '../API/services/alkymersService';
import { setProfilesFilters } from '../redux/actions/profilesActions';
import {
  exportCsvInvitations,
  getScreeningInvitationsStatsCount,
  getScreeningsInvitations,
} from '../API/services/screeningService';
import { setSelectedSearch, setStageCandidates } from '../redux/actions/screeningActions';
import { t } from '../i18n/i18n';
import { setNPSProcessType } from '../redux/actions/npsActions';
import { NPSProcessTypes } from '../constants/enums';

const useFiltersDashboardSearch = (recruitSessionStageId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [previousFilters, setPreviousFilters] = useState(undefined);
  const [loaderTable, setLoaderTable] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const { query } = useSelector((state) => state.profiles);
  const { keepSearchData, selectedSearch } = useSelector((state) => state.screenings);
  const languageKey = useSelector((store) => store.ux.language);
  const [isFirst, setIsFirst] = useState(true);
  const dispatch = useDispatch();
  const appliedFilters = useSelector((state) => state.profiles.appliedFilters);

  const fetchAvailableFilters = async () => {
    setIsLoading(true);
    dispatch(setProfilesFilters({}));
    const filters = await getInvitationsFilters(recruitSessionStageId);
    const stats = await getScreeningInvitationsStatsCount(recruitSessionStageId);
    dispatch(setProfilesFilters(filters));
    dispatch(setSelectedSearch({ ...selectedSearch, stats }));
    setIsLoading(false);
  };
  const loadFilters = async () => {
    setIsLoading(true);
    const filters = await getInvitationsFilters(recruitSessionStageId);
    dispatch(setProfilesFilters(filters));
    setIsLoading(false);
  };

  const fetchStageCandidates = async () => {
    if (!keepSearchData) {
      const queryParams = {};
      appliedFilters.forEach((filter) => {
        if (queryParams[filter.type]) {
          queryParams[filter.type] += `,${filter.id}`;
        } else {
          queryParams[filter.type] = filter.id;
        }
      });

      if (queryParams?.minScore === 0 && queryParams?.maxScore === 100) {
        delete queryParams.minScore;
        delete queryParams.maxScore;
      }

      const mergedQuery = { ...queryParams, ...query };
      const finalQuery = buildSearchQueryWithDefaultOrder(mergedQuery, selectedSearch);

      setLoaderTable(true);
      await getScreeningsInvitations({
        stage: recruitSessionStageId,
        ...finalQuery,
      })
        .then((response) => {
          dispatch(setStageCandidates(response));
        })
        .catch((error) => {
          console.error('Error fetching candidates:', error);
          notification.error({
            message: t('ERROR_FETCHING_CANDIDATES'),
            description: error?.response?.data?.message || t('GENERIC_ERROR_MESSAGE'),
          });
          dispatch(setStageCandidates({ collection: [], pagination: {} }));
        })
        .finally(() => {
          setLoaderTable(false);
        });
    }
  };

  const exportCSV = async () => {
    setLoadingDownload(true);
    const permanentToast = notification.open({
      message: t('EXPORT_CSV_PLEASE_WAIT_DISCLAIMER'),
      type: 'info',
      duration: 0,
    });

    const queryParams = {};
    appliedFilters.forEach((filter) => {
      if (queryParams[filter.type]) {
        queryParams[filter.type] += `,${filter.id}`;
      } else {
        queryParams[filter.type] = filter.id;
      }
    });

    const response = await exportCsvInvitations(recruitSessionStageId, queryParams, languageKey);
    if (response.error) {
      setLoadingDownload(false);
      notification.destroy(permanentToast);
      notification.open({
        message: t(`${response.error}`),
        type: 'error',
      });
    } else {
      const fileURL = window.URL.createObjectURL(new Blob([response]));
      const fileLink = document.createElement('a');
      fileLink.href = fileURL;
      fileLink.setAttribute('download', `${t('DOWNLOAD_INVITATIONS')}.xlsx`);
      fileLink.setAttribute('target', '_blank');
      document.body.appendChild(fileLink);
      fileLink.click();
      setLoadingDownload(false);
      notification.destroy(permanentToast);
      notification.open({
        message: t('EXPORT_CSV_SUCCESSFULLY_DOWNLOADED'),
        type: 'success',
      });
    }
    dispatch(setNPSProcessType(NPSProcessTypes.REPORT_DOWNLOADED));
  };

  useEffect(async () => {
    if (recruitSessionStageId) {
      setIsLoading(true);
      if (!keepSearchData) {
        await fetchAvailableFilters();
      }
      setIsLoading(false);
    }
  }, [recruitSessionStageId]);

  useEffect(() => {
    if (recruitSessionStageId) {
      if (!keepSearchData) {
        if (isFirst) {
          fetchStageCandidates();
          setPreviousFilters(appliedFilters);
          setIsFirst(false);
        } else if (!isEqual(previousFilters, appliedFilters)) {
          fetchStageCandidates();
          setPreviousFilters(appliedFilters);
        }
      }
    }
  }, [recruitSessionStageId, appliedFilters]);

  useEffect(() => {
    if (recruitSessionStageId && !isFirst && !keepSearchData) {
      fetchStageCandidates();
    }
  }, [query?.search]);
  return {
    isLoading,
    loaderTable,
    exportCSV,
    loadingDownload,
    loadFilters,
  };
};

export default useFiltersDashboardSearch;
